import * as React from 'react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { Download, Search } from 'lucide-react';
import { z } from 'zod';

import { Card, CardContent } from '@/components/ui/Card';
import { Input } from '@/components/ui/Input';
import { Label } from '@/components/ui/Label';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { DataTable, type DataColumn } from '@/components/data/DataTable';
import { DateTime } from '@/components/data/DateTime';
import { useAuditEvents } from '@/queries/audit';
import { useApiError } from '@/lib/useApiError';
import { downloadCsv, toCsv } from '@/lib/csv';
import type { AuditEvent } from '@/schemas/admin/audit';
import { useAuthStore } from '@/stores/auth';
import { cn } from '@/lib/cn';

const searchSchema = z.object({
  action: z.string().optional(),
  actorId: z.string().optional(),
  subjectKind: z.string().optional(),
  subjectId: z.string().optional(),
  from: z.string().optional(),
  to: z.string().optional(),
  cursor: z.coerce.number().optional(),
  mineOnly: z.string().optional(),
});

const PAGE_SIZE = 100;

export const Route = createFileRoute('/_authed/audit')({
  validateSearch: searchSchema,
  component: AuditScreen,
});

function AuditScreen() {
  const { t } = useTranslation('audit');
  const search = Route.useSearch();
  const navigate = useNavigate();
  const resolveError = useApiError();
  const userId = useAuthStore((s) => s.userId);

  const query = useAuditEvents();
  const errorInfo = query.error ? resolveError(query.error) : null;

  const filtered = React.useMemo(() => {
    if (!query.data) return [];
    const action = search.action?.toLowerCase();
    const actorId = (search.mineOnly === '1' ? userId : search.actorId) ?? undefined;
    const subjectKind = search.subjectKind?.toLowerCase();
    const subjectId = search.subjectId;
    const from = search.from ? new Date(search.from).getTime() : undefined;
    const to = search.to ? new Date(search.to).getTime() + 24 * 60 * 60 * 1000 : undefined;
    return query.data.filter((e) => {
      if (action && !e.action.toLowerCase().includes(action)) return false;
      if (actorId && e.actor_id !== actorId) return false;
      if (subjectKind && e.subject_kind !== subjectKind) return false;
      if (subjectId && e.subject_id !== subjectId) return false;
      const ts = new Date(e.created_at).getTime();
      if (from && ts < from) return false;
      if (to && ts > to) return false;
      return true;
    });
  }, [query.data, search, userId]);

  const cursor = search.cursor;
  const paged = React.useMemo(() => {
    if (typeof cursor === 'number') {
      return filtered.filter((e) => e.id <= cursor).slice(0, PAGE_SIZE);
    }
    return filtered.slice(0, PAGE_SIZE);
  }, [filtered, cursor]);

  function setSearch(patch: Partial<z.infer<typeof searchSchema>>) {
    void navigate({
      to: '/audit',
      search: (prev) => {
        const next: Record<string, unknown> = { ...prev };
        for (const [k, v] of Object.entries(patch)) {
          if (v === undefined || v === '') delete next[k];
          else next[k] = v;
        }
        return next;
      },
      replace: true,
    });
  }

  function exportCsv() {
    const headers = ['id', 'created_at', 'actor_id', 'action', 'subject_kind', 'subject_id', 'reason'];
    const rows = paged.map((e) => ({
      id: e.id,
      created_at: e.created_at,
      actor_id: e.actor_id ?? '',
      action: e.action,
      subject_kind: e.subject_kind ?? '',
      subject_id: e.subject_id ?? '',
      reason: e.reason ?? '',
    }));
    const csv = toCsv(headers, rows);
    const stamp = new Date().toISOString().replace(/[:.]/g, '-');
    downloadCsv(`audit_${stamp}`, csv);
  }

  function loadOlder() {
    const lastInPage = paged[paged.length - 1];
    if (!lastInPage) return;
    const remaining = filtered.find((e) => e.id < lastInPage.id);
    if (!remaining) return;
    setSearch({ cursor: remaining.id });
  }

  const columns: readonly DataColumn<AuditEvent>[] = [
    {
      id: 'when',
      header: t('columns.when'),
      cell: (r) => <DateTime value={r.created_at} />,
      sortBy: (r) => new Date(r.created_at),
    },
    {
      id: 'action',
      header: t('columns.action'),
      cell: (r) => (
        <Badge tone={r.action.startsWith('admin.pii_reveal') ? 'warn' : 'accent'}>{r.action}</Badge>
      ),
      sortBy: (r) => r.action,
    },
    {
      id: 'actor',
      header: t('columns.actor'),
      cell: (r) => (
        <span dir="ltr" className="font-mono text-xs text-[color:var(--color-fg-muted)]">
          {r.actor_id ? `${r.actor_id.slice(0, 8)}…` : '—'}
        </span>
      ),
    },
    {
      id: 'subject',
      header: t('columns.subject'),
      cell: (r) => (
        <span dir="ltr" className="text-xs">
          <span className="text-[color:var(--color-fg-muted)]">{r.subject_kind ?? '—'}</span>
          {r.subject_id ? (
            <>
              :<span className="font-mono">{r.subject_id.slice(0, 8)}…</span>
            </>
          ) : null}
        </span>
      ),
    },
    {
      id: 'reason',
      header: t('columns.reason'),
      cell: (r) => <span className="text-xs">{r.reason ?? '—'}</span>,
    },
    {
      id: 'id',
      header: t('columns.id'),
      cell: (r) => (
        <span dir="ltr" className="font-mono text-xs text-[color:var(--color-fg-subtle)]">
          {r.id}
        </span>
      ),
      sortBy: (r) => r.id,
      hiddenInitially: true,
    },
  ];

  const hasMore = filtered.length > paged.length;

  return (
    <div className="space-y-6">
      <header className="flex flex-wrap items-center justify-between gap-3">
        <div className="space-y-1">
          <h1 className="text-2xl font-semibold tracking-tight">{t('title')}</h1>
          <p className="text-sm text-[color:var(--color-fg-muted)]">{t('subtitle')}</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button variant="secondary" onClick={exportCsv} disabled={paged.length === 0}>
            <Download className="size-4" aria-hidden />
            {t('exportCsv')}
          </Button>
        </div>
      </header>

      <Card>
        <CardContent className="grid grid-cols-1 gap-3 pt-6 sm:grid-cols-2 lg:grid-cols-4">
          <div>
            <Label htmlFor="audit-action">{t('filters.action')}</Label>
            <div className="relative">
              <Search
                aria-hidden
                className="pointer-events-none absolute start-3 top-1/2 size-4 -translate-y-1/2 text-[color:var(--color-fg-muted)]"
              />
              <Input
                id="audit-action"
                value={search.action ?? ''}
                onChange={(e) => setSearch({ action: e.target.value })}
                placeholder="moderation.hide"
                dir="ltr"
                className="ps-9"
              />
            </div>
          </div>
          <div>
            <Label htmlFor="audit-actor">{t('filters.actor')}</Label>
            <Input
              id="audit-actor"
              value={search.actorId ?? ''}
              onChange={(e) => setSearch({ actorId: e.target.value })}
              placeholder={t('filters.actorPlaceholder')}
              dir="ltr"
            />
          </div>
          <div>
            <Label htmlFor="audit-subject-kind">{t('filters.subjectKind')}</Label>
            <Input
              id="audit-subject-kind"
              value={search.subjectKind ?? ''}
              onChange={(e) => setSearch({ subjectKind: e.target.value })}
              placeholder="user"
              dir="ltr"
            />
          </div>
          <div>
            <Label htmlFor="audit-subject-id">{t('filters.subjectId')}</Label>
            <Input
              id="audit-subject-id"
              value={search.subjectId ?? ''}
              onChange={(e) => setSearch({ subjectId: e.target.value })}
              placeholder={t('filters.subjectIdPlaceholder')}
              dir="ltr"
            />
          </div>
          <div>
            <Label htmlFor="audit-from">{t('filters.from')}</Label>
            <Input
              id="audit-from"
              type="date"
              value={search.from ?? ''}
              onChange={(e) => setSearch({ from: e.target.value })}
              dir="ltr"
            />
          </div>
          <div>
            <Label htmlFor="audit-to">{t('filters.to')}</Label>
            <Input
              id="audit-to"
              type="date"
              value={search.to ?? ''}
              onChange={(e) => setSearch({ to: e.target.value })}
              dir="ltr"
            />
          </div>
          <div className="sm:col-span-2 lg:col-span-2 flex items-end gap-2">
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={search.mineOnly === '1'}
                onChange={(e) => setSearch({ mineOnly: e.target.checked ? '1' : undefined })}
              />
              {t('filters.mineOnly')}
            </label>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() =>
                setSearch({
                  action: undefined,
                  actorId: undefined,
                  subjectKind: undefined,
                  subjectId: undefined,
                  from: undefined,
                  to: undefined,
                  mineOnly: undefined,
                  cursor: undefined,
                })
              }
            >
              {t('filters.clear')}
            </Button>
          </div>
        </CardContent>
      </Card>

      <DataTable<AuditEvent>
        data={paged}
        columns={columns}
        rowKey={(r) => String(r.id)}
        loading={query.isLoading}
        error={errorInfo}
        empty={{ title: t('emptyTitle'), body: t('emptyBody') }}
        rowClassName={(r) =>
          cn(r.action.startsWith('admin.pii_reveal') && 'bg-[color:var(--color-warn-soft)]/30')
        }
        caption={t('cap500')}
        initialSort={{ id: 'when', direction: 'desc' }}
      />

      <div className="flex items-center justify-end gap-2">
        <span className="text-xs text-[color:var(--color-fg-muted)]">
          {t('pageInfo', { count: paged.length, total: filtered.length })}
        </span>
        <Button variant="ghost" size="sm" disabled={!hasMore} onClick={loadOlder}>
          {t('loadOlder')}
        </Button>
      </div>
    </div>
  );
}
